import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

const CANCEL_REASONS = [
  "Falha de comunicação",
  "Cliente desistiu",
  "Outros",
];

export default function CancelFreightDialog({
  open,
  loading = false,
  onClose,
  onConfirm,
  title = "Cancelar frete",
  message = "Deseja mesmo executar esta ação?",
  confirmOnly = false,
  confirmLabel = "Sim",
  cancelLabel = "Não",
}) {
  const [step, setStep] = useState("confirm");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!open) {
      setStep("confirm");
      setReason("");
    }
  }, [open]);

  const handleConfirm = () => {
    if (confirmOnly) {
      onConfirm();
      return;
    }

    if (!reason) return;
    onConfirm(reason);
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: "24px",
          boxShadow: "0px 10px 40px rgba(1, 36, 67, 0.15)",
          overflow: "hidden",
          px: 1,
          py: 1.5,
        },
      }}
    >
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
          pt: 2,
        }}
      >
        <Box
          sx={{
            alignItems: "center",
            backgroundColor: "rgba(255, 49, 0, 0.1)",
            borderRadius: "50%",
            display: "flex",
            height: 60,
            justifyContent: "center",
            mb: 2,
            width: 60,
          }}
        >
          <WarningAmberIcon sx={{ color: "#FF3100", fontSize: 28 }} />
        </Box>

        <DialogTitle
          sx={{
            color: "#012443",
            fontSize: "2rem",
            fontWeight: 700,
            p: 0,
            textAlign: "center",
          }}
        >
          {step === "confirm" ? title : "Motivo do cancelamento"}
        </DialogTitle>
      </Box>

      <DialogContent sx={{ px: 4, pt: 2, textAlign: "center" }}>
        {step === "confirm" ? (
          <Typography
            sx={{
              color: "#555",
              fontSize: "1rem",
              lineHeight: 1.6,
            }}
          >
            {message}
          </Typography>
        ) : (
          <FormControl component="fieldset" sx={{ width: "100%" }}>
            <Typography
              sx={{
                color: "#555",
                fontSize: "1rem",
                lineHeight: 1.6,
                mb: 2.5,
              }}
            >
              Escolha o motivo do cancelamento.
            </Typography>
            <RadioGroup
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              sx={{ gap: 1.5 }}
            >
              {CANCEL_REASONS.map((item) => (
                <FormControlLabel
                  key={item}
                  value={item}
                  control={
                    <Radio
                      sx={{
                        color: "#B8C4D6",
                        "&.Mui-checked": {
                          color: "#FF3100",
                        },
                      }}
                    />
                  }
                  label={item}
                  sx={{
                    backgroundColor: "#FFF",
                    border: "1px solid #E7EDF5",
                    borderRadius: "16px",
                    boxShadow: "0 4px 14px rgba(1, 36, 67, 0.08)",
                    m: 0,
                    minHeight: 56,
                    px: 1.5,
                    textAlign: "left",
                  }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        )}
      </DialogContent>

      <DialogActions
        sx={{ justifyContent: "center", gap: 1.5, px: 4, pb: 3, pt: 1 }}
      >
        {step === "confirm" ? (
          <>
            <Button
              onClick={onClose}
              disabled={loading}
              sx={{
                borderRadius: "50px",
                color: "#012443",
                minWidth: 120,
                px: 3,
                py: 1.1,
                textTransform: "none",
              }}
            >
              {cancelLabel}
            </Button>
            <Button
              variant="contained"
              disableElevation
              onClick={confirmOnly ? handleConfirm : () => setStep("reason")}
              disabled={loading}
              sx={{
                backgroundColor: "#FF3100",
                borderRadius: "50px",
                color: "#FFF",
                minWidth: 120,
                px: 3,
                py: 1.1,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#D62900",
                },
              }}
            >
              {confirmLabel}
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={onClose}
              disabled={loading}
              sx={{
                borderRadius: "50px",
                color: "#012443",
                minWidth: 120,
                px: 3,
                py: 1.1,
                textTransform: "none",
              }}
            >
              Fechar
            </Button>
            <Button
              variant="contained"
              disableElevation
              onClick={handleConfirm}
              disabled={!reason || loading}
              sx={{
                backgroundColor: "#FF3100",
                borderRadius: "50px",
                color: "#FFF",
                minWidth: 140,
                px: 3,
                py: 1.1,
                textTransform: "none",
                "&.Mui-disabled": {
                  backgroundColor: "#FFD3C7",
                  color: "#FFFFFF",
                },
                "&:hover": {
                  backgroundColor: "#D62900",
                },
              }}
            >
              Confirmar
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
